import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { SEOHead } from "@/components/SEOHead";
import { ProductListSkeleton } from "@/components/LoadingSkeleton";

const Category = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data: category } = useQuery({
    queryKey: ["category", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data } = await supabase
        .from("categories")
        .select("id, name, slug")
        .eq("slug", slug)
        .maybeSingle();
      return data;
    },
    enabled: !!slug,
  });

  const { data: products, isLoading } = useQuery({
    queryKey: ["category-products", category?.id],
    queryFn: async () => {
      if (!category?.id) return [] as any[];
      const { data } = await supabase
        .from("product_categories")
        .select("product:products(id, title, description, price_cents, currency, status, product_images(url, sort))")
        .eq("category_id", category.id);
      return (data ?? []).map((r: any) => r.product).filter(Boolean);
    },
    enabled: !!category?.id,
  });
  return (
    <>
      <SEOHead
        title={`${category?.name || slug} Products`}
        description={`Browse ${category?.name || slug} products from local Bahamian vendors on BahaMarket.`}
        keywords={`${category?.name}, Bahamas products, Caribbean ${category?.name}, local vendors`}
      />
      <main className="container mx-auto px-4 py-10">
        <h1 className="text-3xl font-heading font-bold mb-6">Category: {category?.name ?? slug}</h1>
        {isLoading ? (
          <ProductListSkeleton />
        ) : !products?.length ? (
          <p className="text-muted-foreground">No products found in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p: any) => (
              <div key={p.id} className="border rounded-xl p-4 hover:shadow-md transition cursor-pointer" onClick={() => navigate(`/products/${p.id}`)}>
                <div className="aspect-square rounded-md overflow-hidden bg-muted mb-3">
                  {p.product_images?.[0]?.url ? (
                    <img src={p.product_images[0].url} alt={p.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
                  )}
                </div>
                <div className="font-medium mb-1">{p.title}</div>
                <div className="text-sm text-muted-foreground line-clamp-2 mb-2">{p.description}</div>
                <div className="font-semibold">{p.currency} {(p.price_cents/100).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
};
export default Category;